import type { RemoteDragState } from '@/hooks/useRealtimeCollaboration';
import { useAppStore } from '@/stores/appStore';

interface RemoteDragIndicatorProps {
  remoteDrags: Map<string, RemoteDragState>;
}

/**
 * Renders live drag indicators for other users' active drag operations
 * Shows real-time position updates before database persistence
 */
export function RemoteDragIndicator({ remoteDrags }: RemoteDragIndicatorProps) {
  // Clean up stale drag states (older than 5 seconds)
  const now = Date.now();
  const STALE_THRESHOLD_MS = 5000;

  // Get current viewport state to convert floor plan coordinates to screen coordinates
  const viewport = useAppStore(state => state.floorPlan.viewport);

  return (
    <>
      {Array.from(remoteDrags.values()).map(drag => {
        // Skip stale drags
        if (now - drag.timestamp > STALE_THRESHOLD_MS) {
          return null;
        }

        const key = `${drag.elementType}:${drag.elementId}`;

        // Convert floor plan coordinates to viewport screen coordinates
        const screenX = drag.x * viewport.scale + viewport.positionX;
        const screenY = drag.y * viewport.scale + viewport.positionY;

        return (
          <div
            key={key}
            className="pointer-events-none absolute z-50 transition-all duration-100"
            style={{
              left: `${screenX}px`,
              top: `${screenY}px`,
              transform: 'translate(-50%, -50%)',
            }}
          >
            {/* Visual indicator - pulsing ring around element */}
            <div className="relative">
              {/* Outer pulsing ring */}
              <div className="absolute inset-0 -m-2 animate-pulse rounded-lg border-2 border-blue-400 opacity-50" />

              {/* Inner solid ring */}
              <div className="absolute inset-0 -m-1 rounded-lg border-2 border-blue-500" />

              {/* Center dot */}
              <div className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500" />
            </div>

            {/* User name label */}
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-blue-500 px-2 py-1 text-xs font-medium text-white shadow-lg">
              {drag.userName}
            </div>
          </div>
        );
      })}
    </>
  );
}
